import { type FC, useEffect, useRef, useState } from "react";
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { EmailBlockTypeType } from "@/types/graphql/partner.generated";
import {
  BLOCK_TYPE_LABEL_KEYS,
  blockIssues,
  blockSummary,
  DYNAMIC_SOURCE_LABEL_KEYS,
  EDITABLE_BLOCK_TYPES,
  EMAIL_TEMPLATE_LIMITS,
  type EditorBlock,
} from "../model/blocks";
import { BlockEditor } from "./BlockEditor";

interface BlockListProps {
  blocks: EditorBlock[];
  onMove: (from: number, to: number) => void;
  onUpdate: (id: string, patch: Partial<EditorBlock>) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  /** Переменные-адреса кнопок с сервера (marketingLimits.emailUrlVariables). */
  urlVariables?: readonly string[];
}

const NO_URL_VARIABLES: readonly string[] = [];

const hasForm = (block: EditorBlock) =>
  block.type !== EmailBlockTypeType.DividerType &&
  EDITABLE_BLOCK_TYPES.has(block.type);

/**
 * Блоки активного варианта: перетаскивание за ручку, клик раскрывает форму
 * блока. Ручка стоит рядом с ListItemButton, а не внутри: иначе клик по
 * ней раскрывал бы блок, а фокусируемая ручка оказалась бы вложена в
 * кнопку. Родитель монтирует список с key по языку варианта, поэтому
 * «новыми» (и раскрытыми сразу) считаются только добавленные или
 * продублированные блоки, а не блоки другого варианта при переключении.
 */
export const BlockList: FC<BlockListProps> = ({
  blocks,
  onMove,
  onUpdate,
  onDuplicate,
  onRemove,
  urlVariables = NO_URL_VARIABLES,
}) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const knownIds = useRef(new Set(blocks.map((b) => b.id)));

  useEffect(() => {
    const added = blocks.filter((b) => !knownIds.current.has(b.id));
    knownIds.current = new Set(blocks.map((b) => b.id));
    const last = added[added.length - 1];
    if (last && hasForm(last)) setExpandedId(last.id);
  }, [blocks]);

  // Подборка подписывается источником (и пометкой «обязательный»), у
  // остальных блоков короткий текст из blockSummary.
  const summary = (block: EditorBlock) => {
    if (block.type !== EmailBlockTypeType.DynamicCardType || !block.source) {
      return blockSummary(block);
    }
    const source = t(DYNAMIC_SOURCE_LABEL_KEYS[block.source]);
    return block.required
      ? `${source}, ${t("emailTemplates.blocks.requiredShort")}`
      : source;
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    onMove(result.source.index, result.destination.index);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="email-blocks">
        {(droppable) => (
          <List
            component="div"
            disablePadding
            ref={droppable.innerRef}
            {...droppable.droppableProps}
          >
            {blocks.map((block, index) => {
              const issue = blockIssues(block, urlVariables);
              const expanded = expandedId === block.id && hasForm(block);
              return (
                <Draggable key={block.id} draggableId={block.id} index={index}>
                  {(draggable, snapshot) => (
                    <div ref={draggable.innerRef} {...draggable.draggableProps}>
                      <ListItem
                        component="div"
                        disablePadding
                        sx={{
                          bgcolor: snapshot.isDragging
                            ? "action.hover"
                            : "background.paper",
                          borderLeft: 3,
                          borderColor: issue ? "error.main" : "transparent",
                        }}
                        secondaryAction={
                          <Stack direction="row">
                            <Tooltip
                              title={t("emailTemplates.blocks.duplicate")}
                            >
                              <span>
                                <IconButton
                                  aria-label={t(
                                    "emailTemplates.blocks.duplicate"
                                  )}
                                  onClick={() => onDuplicate(block.id)}
                                  disabled={
                                    blocks.length >=
                                    EMAIL_TEMPLATE_LIMITS.blocksPerVariant
                                  }
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t("emailTemplates.blocks.remove")}>
                              <IconButton
                                edge="end"
                                aria-label={t("emailTemplates.blocks.remove")}
                                onClick={() => onRemove(block.id)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                      >
                        <ListItemIcon
                          {...draggable.dragHandleProps}
                          aria-label={t("emailTemplates.blocks.drag")}
                          sx={{
                            minWidth: 36,
                            pl: 1,
                            alignSelf: "stretch",
                            alignItems: "center",
                            cursor: "grab",
                          }}
                        >
                          <DragIndicatorIcon />
                        </ListItemIcon>
                        <ListItemButton
                          onClick={() =>
                            setExpandedId(expanded ? null : block.id)
                          }
                          sx={{ pl: 1, pr: 12 }}
                        >
                          <ListItemText
                            primary={t(BLOCK_TYPE_LABEL_KEYS[block.type])}
                            secondary={
                              issue ? (
                                <Stack
                                  component="span"
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{ color: "error.main" }}
                                >
                                  <ErrorOutlineIcon fontSize="inherit" />
                                  <span>{t(issue)}</span>
                                </Stack>
                              ) : (
                                summary(block)
                              )
                            }
                            slotProps={{ secondary: { noWrap: true } }}
                          />
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={expanded} unmountOnExit>
                        <Stack sx={{ px: 2, py: 2 }}>
                          <BlockEditor
                            block={block}
                            urlVariables={urlVariables}
                            onChange={(patch) => onUpdate(block.id, patch)}
                          />
                        </Stack>
                      </Collapse>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {droppable.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};
